import { apiFetch, httpBlobWithMeta, httpJson } from "./client";

function normalizePhotoRow(row) {
  const src = row && typeof row === "object" ? row : {};
  return {
    id: String(src.id || src.photo_id || "").trim(),
    filename: String(src.filename || src.name || "").trim(),
    room: src.room || null,
    damage_type: src.damage_type || null,
    description: src.description || null,
    confidence: Number.isFinite(Number(src.confidence)) ? Number(src.confidence) : null,
    analysis_source: String(src.analysis_source || "").trim(),
  };
}

export async function createMitigationAssessment(payload) {
  return await httpJson("/mitigation/assessments", {
    method: "POST",
    body: payload,
  });
}

export async function runMitigationAssessment(assessmentId) {
  return await httpJson(`/mitigation/assessments/${encodeURIComponent(String(assessmentId || ""))}/run`, {
    method: "POST",
  });
}

export async function getMitigationAssessment(assessmentId) {
  return await httpJson(`/mitigation/assessments/${encodeURIComponent(String(assessmentId || ""))}`, {
    method: "GET",
  });
}

export async function uploadMitigationPhotos(jobId, files) {
  const id = String(jobId || "").trim();
  if (!id) throw new Error("job_id is required before uploading photos.");
  const list = Array.from(files || []);
  if (list.length < 1) return { photos: [] };
  const form = new FormData();
  list.forEach((file) => form.append("files", file));
  const { data } = await apiFetch(`/jobs/${encodeURIComponent(id)}/photos/upload`, {
    method: "POST",
    body: form,
  });
  const rows = Array.isArray(data?.photos) ? data.photos : [];
  return { photos: rows.map(normalizePhotoRow) };
}

export async function listMitigationPhotos(jobId) {
  const id = String(jobId || "").trim();
  if (!id) return { photos: [] };
  const { data } = await apiFetch(`/jobs/${encodeURIComponent(id)}/photos`, { method: "GET" });
  const rows = Array.isArray(data?.photos) ? data.photos : [];
  return { photos: rows.map(normalizePhotoRow) };
}

export async function deleteMitigationPhotos(jobId, photoIds) {
  const id = String(jobId || "").trim();
  if (!id) throw new Error("job_id is required before deleting photos.");
  const ids = Array.isArray(photoIds) ? photoIds.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!ids.length) return { job_id: id, deleted_ids: [], requested: 0 };
  return await httpJson(`/jobs/${encodeURIComponent(id)}/photos`, {
    method: "DELETE",
    body: { photo_ids: ids },
  });
}

export async function uploadMitigationSketch(assessmentId, file) {
  const out = await uploadMitigationSketches(assessmentId, file ? [file] : []);
  const first = Array.isArray(out?.uploaded) ? out.uploaded[0] : null;
  return {
    assessment_id: out?.assessment_id,
    filename: first?.filename || "",
    content_type: first?.content_type || "",
    uploaded_at: first?.uploaded_at || "",
    analysis: first?.analysis || {},
    affected_rooms: Array.isArray(out?.affected_rooms) ? out.affected_rooms : [],
  };
}

export async function uploadMitigationSketches(assessmentId, files) {
  const id = String(assessmentId || "").trim();
  if (!id) throw new Error("assessment_id is required before uploading sketch.");
  const list = Array.from(files || []);
  if (list.length < 1) throw new Error("At least one sketch file is required.");
  const form = new FormData();
  list.forEach((file) => form.append("files", file));
  return await httpJson(`/mitigation/assessments/${encodeURIComponent(id)}/sketches/upload`, {
    method: "POST",
    body: form,
  });
}

export async function deleteMitigationSketches(assessmentId, sketchIds) {
  const id = String(assessmentId || "").trim();
  if (!id) throw new Error("assessment_id is required before deleting sketches.");
  const ids = Array.isArray(sketchIds) ? sketchIds.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!ids.length) return { assessment_id: id, deleted_ids: [], remaining: null };
  return await httpJson(`/mitigation/assessments/${encodeURIComponent(id)}/sketches/delete`, {
    method: "POST",
    body: { sketch_ids: ids },
  });
}

export async function setMitigationAffectedRooms(assessmentId, affectedRooms) {
  const id = String(assessmentId || "").trim();
  if (!id) throw new Error("assessment_id is required before saving affected rooms.");
  const rooms = Array.isArray(affectedRooms) ? affectedRooms : [];
  return await httpJson(`/mitigation/assessments/${encodeURIComponent(id)}/affected-rooms`, {
    method: "POST",
    body: { affected_rooms: rooms },
  });
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function extractFilenameFromContentDisposition(contentDisposition, fallbackName) {
  const cd = String(contentDisposition || "").trim();
  if (!cd) return fallbackName;
  const utf8Match = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(String(utf8Match[1] || "").trim().replace(/^"|"$/g, "")) || fallbackName;
    } catch (_e) {
      return String(utf8Match[1] || "").trim().replace(/^"|"$/g, "") || fallbackName;
    }
  }
  const simpleMatch = cd.match(/filename\s*=\s*("?)([^";]+)\1/i);
  if (simpleMatch && simpleMatch[2]) {
    return String(simpleMatch[2] || "").trim() || fallbackName;
  }
  return fallbackName;
}

export async function downloadMitigationExportPdf(assessmentId) {
  const id = String(assessmentId || "").trim();
  if (!id) throw new Error("assessment_id is required for PDF export.");
  const { blob, headers } = await httpBlobWithMeta(`/mitigation/assessments/${encodeURIComponent(id)}/export.pdf`, {
    method: "GET",
  });
  const filename = extractFilenameFromContentDisposition(
    headers?.get("content-disposition"),
    `mitigation_assessment_${id}.pdf`
  );
  triggerBlobDownload(blob, filename);
}

export async function downloadMitigationExportXlsx(assessmentId) {
  const id = String(assessmentId || "").trim();
  if (!id) throw new Error("assessment_id is required for Excel export.");
  const { blob, headers } = await httpBlobWithMeta(`/mitigation/assessments/${encodeURIComponent(id)}/export.xlsx`, {
    method: "GET",
  });
  const filename = extractFilenameFromContentDisposition(
    headers?.get("content-disposition"),
    `mitigation_assessment_${id}.xlsx`
  );
  triggerBlobDownload(blob, filename);
}
